import i18n from '../i18n/i18n';
import { stateList } from '@data/states';

const US_CA_STATES = { ...stateList.US.values, ...stateList.CA.values };
const COUNTRY_CODES = [
  'US',
  'AF',
  'AX',
  'AL',
  'DZ',
  'AS',
  'AD',
  'AO',
  'AI',
  'AQ',
  'AG',
  'AR',
  'AM',
  'AW',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BM',
  'BT',
  'BO',
  'BA',
  'BW',
  'BV',
  'BR',
  'IO',
  'BN',
  'BG',
  'BF',
  'BI',
  'KH',
  'CM',
  'CA',
  'CV',
  'KY',
  'CF',
  'TD',
  'CL',
  'CN',
  'CX',
  'CC',
  'CO',
  'KM',
  'CG',
  'CD',
  'CK',
  'CR',
  'CI',
  'HR',
  'CU',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'ET',
  'FK',
  'FO',
  'FJ',
  'FI',
  'FR',
  'GF',
  'PF',
  'TF',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GI',
  'GR',
  'GL',
  'GD',
  'GP',
  'GU',
  'GT',
  'GG',
  'GN',
  'GW',
  'GY',
  'HT',
  'HM',
  'VA',
  'HN',
  'HK',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IM',
  'IL',
  'IT',
  'JM',
  'JP',
  'JE',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KR',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MO',
  'MK',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MQ',
  'MR',
  'MU',
  'YT',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MS',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NL',
  'AN',
  'NC',
  'NZ',
  'NI',
  'NE',
  'NG',
  'NU',
  'NF',
  'MP',
  'NO',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PN',
  'PL',
  'PT',
  'PR',
  'QA',
  'RE',
  'RO',
  'RU',
  'RW',
  'BL',
  'SH',
  'KN',
  'LC',
  'MF',
  'PM',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'GS',
  'ES',
  'LK',
  'SD',
  'SR',
  'SJ',
  'SZ',
  'SE',
  'CH',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TK',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TC',
  'TV',
  'UG',
  'UA',
  'AE',
  'GB',
  'UM',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'VG',
  'VI',
  'WF',
  'EH',
  'YE',
  'ZM',
  'ZW',
];

const codeToNameCache = new Map();
let nameToCodeCache = null;

i18n.on?.('languageChanged', () => {
  codeToNameCache.clear();
});

export function getCountryNameFromCountryCode(code) {
  return codeToNameMap()[code] ?? code;
}

export function getCountryCodeFromCountryName(input) {
  if (!input) return undefined;

  const s = input.trim();
  if (/^[A-Za-z]{2}$/.test(s)) {
    const up = s.toUpperCase();
    return COUNTRY_CODES.includes(up) ? up : undefined;
  }

  const rev = nameToCodeMap();
  if (rev[s]) return rev[s];

  const display = codeToNameMap();
  for (const [code, name] of Object.entries(display)) {
    if (name === s) return code;
  }

  return undefined;
}

export function getStateCodeFromStateName(stateName) {
  if (!stateName) return undefined;

  const s = stateName.trim();
  for (const [code, name] of Object.entries(US_CA_STATES)) {
    if (name === s) return code;
  }

  return undefined;
}

export function countryList() {
  return codeToNameMap();
}

function codeToNameMap() {
  const lang = i18n.language || 'en';
  return codeToNameCache.get(lang) ?? buildCodeToNameMap(lang);
}

function nameToCodeMap() {
  return nameToCodeCache ?? buildNameToCodeMap();
}

function buildCodeToNameMap(lang) {
  const t = getTranslator(lang);
  const map = Object.fromEntries(
    COUNTRY_CODES.map((code) => [code, t(`countries:${code}`)])
  );
  codeToNameCache.set(lang, map);

  return map;
}

function buildNameToCodeMap() {
  if (nameToCodeCache) return nameToCodeCache;

  const tEn = getTranslator('en');
  nameToCodeCache = Object.fromEntries(
    COUNTRY_CODES.map((code) => [tEn(`countries:${code}`), code])
  );

  return nameToCodeCache;
}

function getTranslator(lang) {
  return i18n.getFixedT?.(lang) ?? i18n.t.bind(i18n);
}
